import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { getReviewContextApi } from "src/DAL/coachWorkspace/coachWorkspace";
import { normalizeSubmission } from "src/pages/Trackers/submissionStorage";
import { Breadcrumbs, CW, ErrorBox, InfoHint, Loading, PageTitle, Pill } from "./cwUi";
import CoachNotebook from "./CoachNotebook";
import FeedbackComposer from "./FeedbackComposer";
import PlanSkipDialog from "./PlanSkipDialog";

/**
 * P4.3 — "Feedback for <client>" (design PDF page 7).
 * NEW path /coach/review/:submissionId/feedback (from the review page's
 * Write feedback). Replaces, for the new workspace, the old feedback drawer
 * (ClientFeedbackDrawer), which still works on the old Submission page and
 * via "Classic editor". Sending uses the same feedback API as before.
 */

export default function FeedbackPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [planOpen, setPlanOpen] = useState(false);

  const load = useCallback(async () => {
    setError("");
    const result = await getReviewContextApi(submissionId);
    if (result?.code === 200) setCtx(result);
    else setError(result?.message || "Could not load this check-in");
  }, [submissionId]);

  useEffect(() => {
    setCtx(null);
    load();
  }, [load]);

  const submission = useMemo(() => (ctx ? normalizeSubmission(ctx.submission) : null), [ctx]);

  if (error && !ctx) return <ErrorBox message={error} onRetry={load} />;
  if (!ctx || !submission) return <Loading />;

  const { person, reply, tracker, schedule } = ctx;
  const name = person?.name || [submission.firstName, submission.lastName].filter(Boolean).join(" ") || "Client";
  const backToReview = () => navigate(`/coach/review/${submission.id}`);
  const sent = Boolean(ctx.feedbackSent);

  return (
    <div className="container-fluid">
      <Box sx={{ maxWidth: 1320, mx: "auto", pb: 4 }}>
        <Breadcrumbs
          items={[
            { label: "Today", onClick: () => navigate("/coach/today") },
            {
              label: name,
              onClick:
                person?.memberId || person?.personKey
                  ? () => navigate(`/coach/clients/${encodeURIComponent(person.memberId || person.personKey)}`)
                  : undefined,
            },
            { label: "Review", onClick: backToReview },
            { label: "Feedback" },
          ]}
        />
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start", justifyContent: "space-between", mt: 1, mb: 3 }}>
          <Box sx={{ minWidth: 0 }}>
            <PageTitle>
              Feedback for {name}
              <InfoHint id="feedback.header" />
            </PageTitle>
            <Typography sx={{ fontSize: { xs: 13.5, md: 15 }, color: CW.muted, mt: 0.75 }}>
              {[
                submission.formName || tracker?.cadenceLabel,
                moment(reply?.submittedAt || submission.submittedAt).format("D MMMM YYYY"),
                reply?.reviewDueAt ? `Reply due ${moment(reply.reviewDueAt).format("D MMM, HH:mm")}` : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>
          <Pill tone={sent ? "green" : "amber"} sx={{ fontSize: 13.5, px: 2, py: 0.75 }}>
            {sent ? "Feedback sent · Send more" : "Draft · Not sent"}
          </Pill>
        </Box>

        <FeedbackComposer
          key={submission.id}
          layout="page"
          submission={submission}
          clientName={name}
          initialDraft={ctx.feedbackDraft}
          onClose={backToReview}
          onSent={() => {
            const nextId = ctx.queue?.nextId;
            navigate(nextId ? `/coach/review/${nextId}` : "/coach/today");
          }}
          onOpenPlan={() => setPlanOpen(true)}
          renderNotebook={(footer) => (
            <CoachNotebook memberId={person?.memberId} submissionId={submission.id} variant="feedback" footer={footer} />
          )}
        />
      </Box>
      <PlanSkipDialog
        open={planOpen}
        initialTab="plan"
        onClose={() => setPlanOpen(false)}
        submissionId={submission.id}
        trackerId={tracker?.id}
        personKey={person?.personKey}
        memberId={person?.memberId}
        personName={name}
        reply={reply}
        schedule={schedule}
        onSaved={() => load()}
      />
    </div>
  );
}
